import type { BrowserOptions } from '@sentry/browser'
import type { ErrorMonitor } from '@wix/error-monitor'

import { panoramaClient } from './monitoring/panorama'

// panorama silently drops non-Error arguments, and callers' Error typing is unchecked at runtime
// (tryCatch casts whatever was thrown), so normalize before reporting
const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(`non-error exception: ${String(error)}`)

// attach triage details for axios/wix-http errors in the shape @wix/error-monitor used
// (Grafana trace-link tooling keys off httpErrorDetails.requestId/requestConfig),
// deliberately excluding request/response bodies (PII)
const extractHttpErrorDetails = (error: unknown) => {
  const {
    requestId,
    config: requestConfig,
    response,
  } = (error ?? {}) as {
    requestId?: string
    config?: { url?: string; method?: string; params?: unknown }
    response?: { status?: number; statusText?: string }
  }
  if (!requestConfig && !response) {
    return undefined
  }
  return {
    httpErrorDetails: {
      requestId,
      ...(requestConfig && {
        requestConfig: {
          url: requestConfig.url,
          method: requestConfig.method,
          ...(requestConfig.params !== undefined && { params: JSON.stringify(requestConfig.params) }),
        },
      }),
      ...(response && { response: { status: response.status, statusText: response.statusText } }),
    },
  }
}

type PanoramaErrorMonitor = ReturnType<typeof panoramaClient.errorMonitor>
type PanoramaErrorData = NonNullable<Parameters<PanoramaErrorMonitor['reportError']>[1]>
type PanoramaBreadcrumb = Parameters<PanoramaErrorMonitor['addBreadcrumb']>[0]

// Sentry severity levels share Panorama's ErrorSeverity string values
const toSeverity = (level?: string) => (level ? { severity: level as PanoramaErrorData['severity'] } : undefined)

// Adapts panorama's error monitor to the @wix/error-monitor interface used by
// ErrorMonitorBoundary/ErrorMonitorProvider and the reportError call sites.
const createPanoramaErrorMonitor = (): ErrorMonitor => {
  const monitor = panoramaClient.errorMonitor()
  return {
    captureException: (error, options) => {
      monitor.reportError(toError(error), {
        tags: options?.tags,
        context: { ...extractHttpErrorDetails(error), ...options?.contexts },
        ...toSeverity(options?.level),
      })
      return ''
    },
    captureMessage: (message, options) => {
      // panorama silently drops non-Error arguments, so wrap the message
      monitor.reportError(new Error(message), {
        tags: options?.tags,
        context: options?.contexts,
        ...toSeverity(options?.level),
      })
      return ''
    },
    addBreadcrumb: breadcrumb => {
      monitor.addBreadcrumb(breadcrumb as PanoramaBreadcrumb)
    },
  }
}

export const errorMonitor: ErrorMonitor = createPanoramaErrorMonitor()

// Panorama's Sentry client accepts no filtering options, but its default InboundFilters
// integration re-reads the client options on every event, so they can be set post-creation.
// The hub is panorama's own (sentryMain: true) by the time this module evaluates.
const sentryClientOptions = window.Sentry?.getCurrentHub().getClient()?.getOptions() as BrowserOptions | undefined
if (sentryClientOptions) {
  sentryClientOptions.ignoreErrors = [
    'conductExperiment?key=specs.fedinfra.Glassbox',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'logMutedMessage',
    'Permission denied to access property "unlock"',
    'Could not establish connection. Receiving end does not exist.',
    'Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target',
  ]
  sentryClientOptions.denyUrls = [
    /^(chrome|moz)-extension:\/\//,
    /safari-(web-)?extension/,
    /^webpack-internal:\/\//,
    /^\/\/hidden\//,
    /^<anonymous>/,
  ]
}
window.Sentry?.setUser({ id: window.__USER_ID__ })

// drop stackless UnhandledRejection noise (restores the old RemoveTrash processor);
// global processors run before panorama's beforeSend, so these never reach Sentry or panorama BI
window.Sentry?.addGlobalEventProcessor(event => {
  const value = event.exception?.values?.[0]
  if (value && value.type === 'UnhandledRejection' && !value.stacktrace) {
    return null
  }
  return event
})
